import axios from 'axios';

export const STORAGE_KEYS = {
  token: 'JWT-TOKEN',
  tenant: 'TENANT',
  firm: 'FIRM',
  domain: 'DOMAIN',
  host: 'CONTEXT_HOST',
};

const AUTH_USER_CACHE_KEYS = [
  'userProfile',
  'uid',
  'addresses',
  'phones',
  'emails',
  'groups',
  'roles',
  'titles',
  'profileDropdownConfig',
  'permissions',
  'AVATAR',
];

let demoShowcaseInterceptorInstalled = false;

const isDemoShowcasePage = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  return new URLSearchParams(window.location.search).get('demoShowcase') === 'true';
};

const demoShowcasePersona = () => {
  if (typeof window === 'undefined') {
    return '';
  }
  return new URLSearchParams(window.location.search).get('demoPersonaId') || '';
};

const appendDemoParams = (config) => {
  const currentParams = config.params || {};
  if (currentParams instanceof URLSearchParams) {
    currentParams.set('demoShowcase', 'true');
    currentParams.set('demoPersonaId', demoShowcasePersona());
    return currentParams;
  }
  return {
    ...currentParams,
    demoShowcase: 'true',
    demoPersonaId: demoShowcasePersona(),
  };
};

const resolveDemoEndpoint = (pathname) => {
  if (pathname.includes('/platform-showcase-demo/')) {
    return '';
  }
  if (pathname.startsWith('/main/entities')) {
    return '/main/platform-showcase-demo/entities';
  }
  if (pathname.startsWith('/main/cart/cartSummary')) {
    return '/main/platform-showcase-demo/cartSummary';
  }
  if (pathname === '/main/cart' || pathname.startsWith('/main/cart/anonymous')) {
    return '/main/platform-showcase-demo/cart';
  }
  if (pathname.startsWith('/main/orders')) {
    return '/main/platform-showcase-demo/orders';
  }
  if (pathname.startsWith('/main/requests')) {
    return '/main/platform-showcase-demo/requests';
  }
  return '';
};

export const installDemoShowcaseAxiosInterceptor = () => {
  if (demoShowcaseInterceptorInstalled) {
    return;
  }
  demoShowcaseInterceptorInstalled = true;
  axios.interceptors.request.use((config) => {
    if (!isDemoShowcasePage() || String(config.method || 'get').toLowerCase() !== 'get') {
      return config;
    }
    const resolvedUrl = new URL(config.url || '', window.location.origin);
    const demoEndpoint = resolveDemoEndpoint(resolvedUrl.pathname);
    if (!demoEndpoint) {
      return config;
    }
    config.url = `${resolvedUrl.origin}${demoEndpoint}`;
    config.params = appendDemoParams(config);
    config.headers = {
      ...(config.headers || {}),
      'X-OpenGrit-Demo-Showcase': 'true',
    };
    return config;
  });
};

export const getStoredToken = () => localStorage.getItem(STORAGE_KEYS.token);

export const getCurrentHost = () =>
  typeof window !== 'undefined' ? window.location.host : undefined;

const clearTenantDomainContext = () => {
  localStorage.removeItem(STORAGE_KEYS.tenant);
  localStorage.removeItem(STORAGE_KEYS.firm);
  localStorage.removeItem(STORAGE_KEYS.domain);
};

export const ensureHostScopedRequestContext = () => {
  const currentHost = getCurrentHost();
  const storedHost = localStorage.getItem(STORAGE_KEYS.host);

  if (storedHost && currentHost && storedHost !== currentHost) {
    clearTenantDomainContext();
    localStorage.removeItem(STORAGE_KEYS.token);
  }

  if (currentHost) {
    localStorage.setItem(STORAGE_KEYS.host, currentHost);
  }
};

export const getStoredTenantId = () =>
  localStorage.getItem(STORAGE_KEYS.tenant) || localStorage.getItem(STORAGE_KEYS.firm);

export const getStoredDomainId = () => localStorage.getItem(STORAGE_KEYS.domain);

export const syncAxiosRequestContext = () => {
  ensureHostScopedRequestContext();

  const token = getStoredToken();
  const tenant = getStoredTenantId();
  const domain = getStoredDomainId();

  if (token) {
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common.Authorization;
  }

  if (tenant) {
    axios.defaults.headers.common.tenant = tenant;
    axios.defaults.headers.common.cname = tenant;
  } else {
    delete axios.defaults.headers.common.tenant;
    delete axios.defaults.headers.common.cname;
  }

  if (domain) {
    axios.defaults.headers.common.domain = domain;
  } else {
    delete axios.defaults.headers.common.domain;
  }
};

export const persistTenantDomainContext = ({ tenantId, domainId }) => {
  ensureHostScopedRequestContext();

  if (tenantId) {
    localStorage.setItem(STORAGE_KEYS.tenant, tenantId);
    localStorage.setItem(STORAGE_KEYS.firm, tenantId);
  }

  if (domainId) {
    localStorage.setItem(STORAGE_KEYS.domain, domainId);
  } else {
    localStorage.removeItem(STORAGE_KEYS.domain);
  }

  syncAxiosRequestContext();
};

export const persistAuthSession = ({ token, tenantId, domainId }) => {
  ensureHostScopedRequestContext();

  if (token) {
    localStorage.setItem(STORAGE_KEYS.token, token);
  }

  persistTenantDomainContext({ tenantId, domainId });
};

export const clearAuthSession = () => {
  Object.values(STORAGE_KEYS).forEach((key) => localStorage.removeItem(key));
  AUTH_USER_CACHE_KEYS.forEach((key) => localStorage.removeItem(key));
  syncAxiosRequestContext();
};
